import { apiRequest, apiRequestWithEtag, type IEtagged } from '@/domains/api';
import type {
  IContentPost,
  IContentPostBody,
  IContentPostQuery
} from './post-contracts';

/**
 * survey-api's website posts — `/api/v1/admin/website/posts` — and the sole reader of them.
 *
 * **Concurrency is the server's, quoted back as read.** Every write but the create carries the
 * post's revision as `If-Match`: survey-api answers 428 without it and 409 `revision-conflict`
 * on a stale one, and the answer to a write carries the next revision as its own `ETag`. The
 * value is opaque here — it is never parsed, compared or incremented on this side — which is
 * the rule the work-item and role modules follow and the reason a screen cannot drift out of
 * step with how survey-api spells a revision.
 *
 * **Both filters are the server's.** They go on the query string rather than into a `.filter()`
 * after the fact, because a client-side filter is one that silently filters a page.
 *
 * Nothing here derives a slug, decides whether a post may be published, or judges a body: the
 * address is frozen by the server once a post is live, the publish routes carry their own
 * permission, and the body is sanitised on the way in and answered back as stored.
 */

const BASE = '/api/v1/admin/website/posts';

function postPath(postId: string): string {
  return `${BASE}/${encodeURIComponent(postId)}`;
}

function query(filters: IContentPostQuery): string {
  const params = new URLSearchParams();
  if (filters.kind) params.set('kind', filters.kind);
  if (filters.status) params.set('status', filters.status);
  const search = params.toString();
  return search ? `?${search}` : '';
}

/** The header every write but the create carries, from the `ETag` the read handed back. */
function precondition(etag: string): { 'If-Match': string } {
  return { 'If-Match': etag };
}

export const postsApi = {
  list(filters: IContentPostQuery, signal?: AbortSignal): Promise<IContentPost[]> {
    return apiRequest<IContentPost[]>(`${BASE}${query(filters)}`, { signal });
  },

  /** One post with its history, and the `ETag` its next write quotes. */
  read(postId: string, signal?: AbortSignal): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(postPath(postId), { signal });
  },

  create(body: IContentPostBody): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(BASE, { method: 'POST', body });
  },

  save(postId: string, etag: string, body: IContentPostBody): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(postPath(postId), {
      method: 'PUT',
      body,
      headers: precondition(etag)
    });
  },

  publish(postId: string, etag: string): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(`${postPath(postId)}/publication`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  unpublish(postId: string, etag: string): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(`${postPath(postId)}/publication`, {
      method: 'DELETE',
      headers: precondition(etag)
    });
  },

  /** WEB-13 AC1 — "I have finished". It changes no status and gates nothing. */
  submit(postId: string, etag: string): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(`${postPath(postId)}/submission`, {
      method: 'POST',
      headers: precondition(etag)
    });
  },

  /** WEB-14 AC2 — restores a version's content as a new one. Nothing is rewound. */
  revert(postId: string, etag: string, versionNo: number): Promise<IEtagged<IContentPost>> {
    return apiRequestWithEtag<IContentPost>(`${postPath(postId)}/reverts`, {
      method: 'POST',
      body: { versionNo },
      headers: precondition(etag)
    });
  }
};
